/**
 * URL shape helpers.
 *
 * The internal viewId format is namespaced (e.g. `inventory.view.transactions`)
 * so descriptors stay collision-proof across federated modules. The URL the
 * operator sees should be short and human-friendly:
 *
 *   /tyndo.inventory/view/inventory.view.transactions   ← old (still supported)
 *   /tyndo.inventory/transactions                       ← new (preferred)
 *
 * These helpers convert between the two — `viewSlugForUrl` produces the URL
 * form, `resolveSlugToViewId` looks the slug up against a list of registered
 * view ids when the route resolver can't match the slug directly.
 *
 * Same idea for the breadcrumb: the URL slug `transactions` should display
 * as "Transactions" (taken from the descriptor's `label`), not as the
 * cap-cased URL segment.
 */

/**
 * Convert a registered viewId into the short slug we use in URLs. The slug
 * is just the last dot-segment, so descriptors keep their `module.view.x`
 * shape but URLs stay short.
 *
 *   `inventory.view.transactions` → `transactions`
 *   `transactions`                → `transactions`  (idempotent)
 */
export function viewSlugForUrl(viewId: string | undefined | null): string {
  if (!viewId) return '';
  const idx = viewId.lastIndexOf('.');
  return idx >= 0 ? viewId.slice(idx + 1) : viewId;
}

/**
 * Given a URL slug and the set of viewIds registered for the current
 * module, resolve back to the canonical viewId. Used at route-parse time
 * so the existing viewRegistry lookup doesn't have to know about slugs.
 *
 * Resolution order:
 *   1. exact match (legacy URLs that still pass full viewId)
 *   2. suffix match against `.{slug}` (new URLs that drop the prefix)
 *   3. case-insensitive suffix match (defensive)
 *   4. return the slug itself, letting the caller fail with a clean
 *      "view not found" empty state
 */
export function resolveSlugToViewId(
  slug: string | undefined | null,
  candidates: readonly string[],
): string {
  if (!slug) return '';
  if (candidates.includes(slug)) return slug;
  const dotSuffix = '.' + slug;
  const suffix = candidates.find((id) => id.endsWith(dotSuffix));
  if (suffix) return suffix;
  const lower = slug.toLowerCase();
  const ci = candidates.find((id) => id.toLowerCase().endsWith('.' + lower));
  return ci ?? slug;
}

/**
 * Friendly module name from `tyndo.inventory` → `Inventory`. We strip the
 * `tyndo.` (or any other provider) prefix so breadcrumbs read cleanly.
 */
export function friendlyModuleLabel(
  moduleId: string | undefined | null,
  registeredLabel?: string | null,
): string {
  if (registeredLabel) return registeredLabel;
  if (!moduleId) return '';
  const idx = moduleId.lastIndexOf('.');
  const last = idx >= 0 ? moduleId.slice(idx + 1) : moduleId;
  return last.charAt(0).toUpperCase() + last.slice(1);
}
